import { Injectable } from "@angular/core";
import { EstatisticasLeitura } from "../../models/usuario/EstatisticasLeitura";

import {
  BehaviorSubject,
  catchError,
  first,
  firstValueFrom,
  Observable,
  of,
  tap,
} from "rxjs";
import { AppConfig } from "src/app/app.config";
import { HttpClient, HttpParams } from "@angular/common/http";
import { ErrorHandlerService } from "../errorHandler.service";
import { EnumTipoObjeto } from "src/app/enums/EnumTipoObjeto";
import { OperacaoSignalR } from "../../models/OperacaoSignalR";
import { SignalrService } from "../signalr.service";
import { StorageService } from "../storage.service";
import { UsuarioPreferenciasService } from "./usuario.preferencias.service";
import { DataControleRepositorio } from "src/app/repositorios/dataControle.repositorio";
import { EnumTipoDataControle } from "src/app/enums/EnumDataControle";

@Injectable()
export class UsuarioEstatisticasService {
  readonly databaseName = "svdm";
  readonly collectionName = "estatisticas";
  private leisFavoritas: string[] = [];
  private leisFavoritas$ = new BehaviorSubject<string[]>(this.leisFavoritas);

  constructor(
    private signalrService: SignalrService,
    private usuarioPreferenciasService: UsuarioPreferenciasService,
    private errorHandlerService: ErrorHandlerService,
    private httpClient: HttpClient,
    private storageService: StorageService,
    private dataControleRepositorio: DataControleRepositorio
  ) {}

  public async carregarEstatisticasServicePorLei(
    leiId: string
  ): Promise<EstatisticasLeitura> {
    const url = `${AppConfig.apiEndpoint}/DadosCadastroUsuario/EstatisticasLeituraPorLei`;
    let requestParams = new HttpParams();
    let estatisticasLeitura: EstatisticasLeitura;
    const estatisticasLeituralocal =
      await this.storageService.getByKey<EstatisticasLeitura>(
        leiId,
        this.databaseName,
        this.collectionName
      );

    if (estatisticasLeituralocal) {
      const cache = await this.VerificaCacheEstatisticas();

      if (cache) {
        return estatisticasLeituralocal;
      }
    }

    requestParams = requestParams.append("leiId", leiId);
    requestParams = requestParams.append(
      "usuarioId",
      this.usuarioPreferenciasService.getConfiguracoesvalue.id
    );
    await firstValueFrom(
      this.httpClient
        .get<EstatisticasLeitura>(url, { params: requestParams })
        .pipe(
          tap({
            next: (result: EstatisticasLeitura) => {
              if (result) {
                this.atualizar(result);
                estatisticasLeitura = result;
                this.dataControleRepositorio
                  .salvar(
                    this.dataControleRepositorio.generateDataControleTipo(
                      EnumTipoDataControle.DataVerificacaoCacheEstatisticas
                    )
                  )
                  .then(() => {})
                  .catch((error) =>
                    this.errorHandlerService.handleError(error)
                  );
              } else {
                estatisticasLeitura = new EstatisticasLeitura();
              }
            },
            error: (error) => {
              this.errorHandlerService.handleError(error);
              if (estatisticasLeituralocal) {
                estatisticasLeitura = estatisticasLeituralocal;
              } else {
                estatisticasLeitura = new EstatisticasLeitura();
              }
            },
          }),
          // o tap não engole o erro: sem isso a falha subia e derrubava o
          // carregamento da lei inteira, mesmo com o dado local já atribuído
          catchError(() => of(null))
        )
    );
    return estatisticasLeitura;
  }

  public async fromNuvem(estatisticas: EstatisticasLeitura[]): Promise<void> {
    try {
      await this.storageService.upsertMany(
        estatisticas,
        this.databaseName,
        this.collectionName,
        false
      );
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  public async salvarLote(
    listaEstatisticas: EstatisticasLeitura[],
    sync = true
  ): Promise<void> {
    const promises = new Array<Promise<void>>();
    for (const estatisticas of listaEstatisticas) {
      promises.push(this.atualizar(estatisticas));
    }
    await Promise.all(promises);
    if (sync) {
      const mensagens = listaEstatisticas.map((estatisticas) => {
        const msg = new OperacaoSignalR();
        msg.dados = estatisticas;
        return msg;
      });

      await this.signalrService.enviarMensagens(
        mensagens,
        EnumTipoObjeto.Estatisticas
      );
    }
  }

  public async marcarLido(
    idLei: string,
    idLinha: string
  ): Promise<EstatisticasLeitura> {
    const estatisticas = await this.buscar(idLei);

    const iLinha = estatisticas.linhasLidas.findIndex((l) => l === idLinha);
    if (iLinha === -1) {
      estatisticas.linhasLidas.push(idLinha);
    } else {
      estatisticas.linhasLidas.splice(iLinha, 1);
    }

    await this.atualizar(estatisticas);
    return estatisticas;
  }

  public async marcarVariosLidos(
    idLei: string,
    idsLinhas: string[]
  ): Promise<EstatisticasLeitura> {
    const estatistica = await this.buscar(idLei);

    idsLinhas.forEach((idLinha) => {
      const iLinha = estatistica.linhasLidas.findIndex((l) => l === idLinha);
      if (iLinha === -1) {
        estatistica.linhasLidas.push(idLinha);
      }
    });

    await this.atualizar(estatistica);
    return estatistica;
  }

  public async marcarFavorito(id: string): Promise<EstatisticasLeitura> {
    const estatisticas = await this.buscar(id);
    estatisticas.favorito = !estatisticas.favorito;

    await this.atualizar(estatisticas);
    await this.listar();

    return estatisticas;
  }

  public async alterarPosicaoLeitura(idLei: string, idLinha: string) {
    const estatisticas = await this.buscar(idLei);
    estatisticas.idUltimoItemLido = idLinha;

    await this.atualizar(estatisticas);
  }

  public async atualizar(
    estatistica: EstatisticasLeitura,
    sync = true
  ): Promise<void> {
    try {
      const url = `${AppConfig.apiEndpoint}/DadosCadastroUsuario/EstatisticasLeituras`;

      await firstValueFrom(
        this.httpClient.post(url, estatistica).pipe(first())
      );

      await this.storageService.upsert(
        estatistica,
        this.databaseName,
        this.collectionName
      );
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  public async buscar(idLei: string): Promise<EstatisticasLeitura> {
    try {
      if (!idLei || idLei.trim() === "") {
        return new EstatisticasLeitura();
      }
      const estatistica =
        await this.storageService.getByKey<EstatisticasLeitura>(
          idLei,
          this.databaseName,
          this.collectionName
        );

      if (estatistica) {
        return estatistica;
      } else {
        return new EstatisticasLeitura();
      }
    } catch (error) {
      this.errorHandlerService.handleError(error);
      return new EstatisticasLeitura();
    }
  }

  public async listar(): Promise<EstatisticasLeitura[]> {
    try {
      const listaEstatisticas =
        await this.storageService.list<EstatisticasLeitura>(
          this.databaseName,
          this.collectionName
        );

      this.leisFavoritas = listaEstatisticas
        .filter((l) => l.favorito)
        .map((l) => l.id);
      this.updateLeisFavoritas();
      return listaEstatisticas;
    } catch (error) {
      this.errorHandlerService.handleError(error);
      return [];
    }
  }

  updateLeisFavoritas() {
    this.leisFavoritas$.next(this.leisFavoritas);
  }

  getLeisFavoritas(): Observable<string[]> {
    return this.leisFavoritas$.asObservable();
  }
  private async VerificaCacheEstatisticas(): Promise<boolean> {
    const lastDocumentsListCheck = await this.dataControleRepositorio.buscar(
      EnumTipoDataControle.DataVerificacaoCacheEstatisticas
    );
    const tempoEmMinutos = AppConfig.cacheConteudo;

    if (lastDocumentsListCheck) {
      const cacheConteudo: number = tempoEmMinutos * 60 * 1000;
      const ultimaModificacao: Date =
        lastDocumentsListCheck.dataHoraModificacao;
      const DataAtual: Date = new Date();

      if (DataAtual.getTime() - ultimaModificacao.getTime() < cacheConteudo) {
        return true;
      }
    }

    return false;
  }
}
